import { useFieldApi } from 'informed';
import { taggedTemplateLiteral as _taggedTemplateLiteral, objectWithoutProperties as _objectWithoutProperties, extends as _extends, objectDestructuringEmpty as _objectDestructuringEmpty } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import styled from '../../theming/styled-components.js';
import themes from '../../theming/themes.js';
import React, { useContext, useRef, useEffect } from 'react';
import _Icon from '../../components/Icon/index.js';
import _Collapse from '../../components/Collapse/index.js';
import { FileType } from '../../types/FileType.js';

var _excluded = ["checkbox", "onCheckboxChange", "directoryItem", "foldersOnly", "onItemSelect", "defaultOpen", "theme", "child"];
var _templateObject, _templateObject2;
var LegacyFileItem = styled.button(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  border: none;\n  display: block;\n  padding: 0.5rem 0.75rem;\n  font-size: 1rem;\n  &:hover {\n    cursor: pointer;\n    text-decoration: underline;\n  }\n  &:focus {\n    outline: none;\n    text-decoration: underline;\n  }\n"])));
var FileItem = styled.button(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  border: none;\n  display: block;\n  font-size: 1rem;\n  background-color: transparent;\n  padding-left: 2.5rem;\n  margin-top: 0.5rem;\n  &:hover {\n    cursor: pointer;\n    text-decoration: underline;\n  }\n  &:focus {\n    outline: none;\n    text-decoration: underline;\n  }\n"])));
var defaultDirElemContext = {
  deregisterChild: function deregisterChild() {},
  registerChild: function registerChild() {},
  setChecked: function setChecked(value) {}
};
// i dunno about all this default stuff (we didn't need in code sandbox? maybe just typescript being picky)
var DirectoryElementContext = /*#__PURE__*/React.createContext(defaultDirElemContext);
/**
 * Renders an element of a Directory, as either a file or a folder
 * Folders found will trigger a recursive call to DirectoryElement
 */
var _DirectoryElement = function DirectoryElement(_ref) {
  var checkbox = _ref.checkbox,
    onCheckboxChange = _ref.onCheckboxChange,
    directoryItem = _ref.directoryItem,
    foldersOnly = _ref.foldersOnly,
    onItemSelect = _ref.onItemSelect,
    defaultOpen = _ref.defaultOpen,
    theme = _ref.theme,
    child = _ref.child,
    rest = _objectWithoutProperties(_ref, _excluded);
  var checkboxField = directoryItem.id;
  // we know the field we're targetting is going to be the directoryItem.id
  var fieldApi = useFieldApi(checkboxField);
  // grab the context of the parent DirectoryItem
  var parentContext = useContext(DirectoryElementContext);
  // place to store refs to the context of any children
  var childrenRefs = useRef([]);
  // define the current context
  var myContext = {
    deregisterChild: function deregisterChild(childRef) {
      // I think this works
      if (childrenRefs.current) {
        var i = childrenRefs.current.indexOf(childRef);
        childrenRefs.current.splice(i, 1);
      }
    },
    registerChild: function registerChild(childRef) {
      if (childrenRefs.current) {
        childrenRefs.current.push(childRef);
      }
    },
    setChecked: function setChecked(value) {
      if (fieldApi.exists() && value !== fieldApi.getValue()) {
        fieldApi.setValue(value);
      }
    }
  };
  /**
   * Registers myContext as a child of the parentContext, and sets up deregister
   */
  useEffect(function () {
    if (parentContext) {
      parentContext.registerChild(myContext);
    }
    return function () {
      parentContext.deregisterChild(myContext);
    };
  }, []);
  var _onItemSelect = function _onItemSelect() {
    // execute passed in onItemSelect behavior
    if (onItemSelect) {
      onItemSelect(directoryItem);
    }
  };
  var _onCheckboxChange = function _onCheckboxChange(value) {
    // IF CHECKED TRUE - trigger checkbox toggle of parent (will use default no-op if no context available)
    if (value) {
      parentContext.setChecked(value);
    }
    // IF CHECKED FALSE - trigger checkbox toggle of all children (will use default no-op if no context available)
    if (childrenRefs.current && !value) {
      childrenRefs.current.forEach(function (childContext) {
        childContext.setChecked(value);
      });
    }
    // execute passed in onCheckboxChange behavior
    if (onCheckboxChange) {
      onCheckboxChange(value, directoryItem);
    }
  };
  var LFGExpandedIcon = function LFGExpandedIcon(_ref2) {
    _objectDestructuringEmpty(_ref2);
    return /*#__PURE__*/React.createElement(_Icon, {
      name: 'chevron-up-solid',
      theme: themes.secureSite,
      size: 'xs',
      color: 'secondaryDark'
    });
  };
  var LFGCollapsedIcon = function LFGCollapsedIcon(_ref3) {
    _objectDestructuringEmpty(_ref3);
    return /*#__PURE__*/React.createElement(_Icon, {
      name: 'chevron-down-solid',
      theme: themes.secureSite,
      size: 'xs',
      color: 'secondaryDark'
    });
  };
  if (directoryItem.type === FileType.DIR) {
    var children = directoryItem.children.map(function (_directoryItem, index) {
      return /*#__PURE__*/React.createElement(_DirectoryElement, {
        checkbox: checkbox,
        key: index,
        directoryItem: _directoryItem,
        foldersOnly: foldersOnly,
        onCheckboxChange: onCheckboxChange,
        onItemSelect: onItemSelect,
        theme: theme,
        child: true
      });
    });
    // this is a folder
    return /*#__PURE__*/React.createElement(DirectoryElementContext.Provider, {
      value: myContext
    }, /*#__PURE__*/React.createElement(_Collapse, _extends({
      isOpen: defaultOpen,
      checked: directoryItem.checked,
      checkboxChange: _onCheckboxChange,
      checkboxField: checkbox ? checkboxField : undefined,
      type: "folder",
      headerClick: _onItemSelect,
      title: directoryItem.label,
      expandedIcon: theme === themes.secureSite ? LFGExpandedIcon : undefined,
      collapsedIcon: theme === themes.secureSite ? LFGCollapsedIcon : undefined,
      theme: theme,
      child: child
    }, rest), children));
  } else if (!foldersOnly) {
    // this is a file
    if (theme === themes.secureSite) {
      return /*#__PURE__*/React.createElement(FileItem, {
        onClick: _onItemSelect
      }, /*#__PURE__*/React.createElement(_Icon, {
        name: 'documents',
        className: 'mr-2'
      }), directoryItem.label);
    } else {
      return /*#__PURE__*/React.createElement(LegacyFileItem, {
        onClick: _onItemSelect
      }, /*#__PURE__*/React.createElement(_Icon, {
        style: {
          marginLeft: '2rem'
        },
        name: "documents",
        className: "mr-2"
      }), directoryItem.label);
    }
  } else {
    // file but we won't show
    return null;
  }
};

export default _DirectoryElement;
export { DirectoryElementContext };
